import React, {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Flex,
    Text,
} from "@chakra-ui/react";

import {
    Plus,
    Eye,
    Edit,
    Trash2,
    EllipsisVertical,
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import ReusableTable from "components/Tables/ReusableTable";
import GradientButton from "components/Buttons/GradientButton";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";

import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";

import AddAgentService from "./components/AddAgentService";
import EditAgentService from "./components/EditAgentService";
import ViewAgentService from "./components/ViewAgentService";
import DeleteAgentService from "./components/DeleteAgentService";

import {
    fetchAgentServicesAction,
    deleteAgentServiceAction,
} from "state/actions/agentServiceActions";

import { useCustomNotification } from "components/Toasts/OpenNotification";


const AgentServiceManagement = () => {

    const dispatch = useDispatch();

    const notify =
        useCustomNotification();

    const {
        services = [],
        loading = false,
    } = useSelector(
        (state) =>
            state.agentServices || {}
    );



    const [
        isAddOpen,
        setIsAddOpen,
    ] = useState(false);


    const [
        isEditOpen,
        setIsEditOpen,
    ] = useState(false);


    const [
        isViewOpen,
        setIsViewOpen,
    ] = useState(false);


    const [
        isDeleteOpen,
        setIsDeleteOpen,
    ] = useState(false);


    const [
        selectedService,
        setSelectedService,
    ] = useState(null);



    useEffect(() => {

        dispatch(
            fetchAgentServicesAction()
        );

    }, [dispatch]);



    const handleAdd = () => {

        setSelectedService(null);

        setIsAddOpen(true);
    };



    const handleView = (
        service
    ) => {

        setSelectedService(
            service
        );

        setIsViewOpen(true);
    };


   
    const handleEdit = (
        service
    ) => {

        setSelectedService(
            service
        );

        setIsEditOpen(true);
    };



    const handleDelete = (
        service
    ) => {

        setSelectedService(
            service
        );

        setIsDeleteOpen(true);
    };



    const handleConfirmDelete =
        async () => {

            if (
                !selectedService?.service_id
            ) {

                notify({

                    message:
                        "Service Missing",

                    description:
                        "Unable to identify the service.",

                    type:
                        "error",

                });

                return;
            }


            const response =
                await dispatch(
                    deleteAgentServiceAction(
                        selectedService.service_id
                    )
                );


            if (
                response.success
            ) {

                notify({

                    message:
                        "Service Deleted",

                    description:
                        "Agent service deleted successfully.",

                    type:
                        "success",

                });


                setIsDeleteOpen(
                    false
                );

                setSelectedService(
                    null
                );

            } else {

                notify({

                    message:
                        "Failed to Delete Service",

                    description:
                        response.error ||
                        "Something went wrong while deleting the service.",

                    type:
                        "error",

                });

            }
        };


    const generateMenuItems =
        (row) => {

            const service =
                row.original;


            return [

                {
                    icon:
                        <Eye
                            size={16}
                        />,

                    text:
                        "View Service",

                    onClick:
                        () =>
                            handleView(
                                service
                            ),
                },


                {
                    icon:
                        <Edit
                            size={16}
                        />,

                    text:
                        "Edit Service",

                    onClick:
                        () =>
                            handleEdit(
                                service
                            ),
                },


                {
                    icon:
                        <Trash2
                            size={16}
                        />,

                    text:
                        "Delete Service",

                    onClick:
                        () =>
                            handleDelete(
                                service
                            ),
                },

            ];
        };



    const columnsData = [

        {
            Header:
                "SERVICE NAME",

            accessor:
                "name",

            Cell:
                ({
                    value,
                }) => (

                    <Text
                        fontWeight="600"
                        noOfLines={1}
                    >
                        {
                            value ||
                            "N/A"
                        }
                    </Text>

                ),
        },


        {
            Header:
                "DESCRIPTION",

            accessor:
                "description",

            Cell:
                ({
                    value,
                }) => (

                    <Text
                        color="gray.600"
                        fontSize="sm"
                        noOfLines={2}
                    >
                        {
                            value ||
                            "N/A"
                        }
                    </Text>

                ),
        },


        {
            Header:
                "STATUS",

            accessor:
                "is_active",

            Cell:
                ({
                    value,
                }) => {

                    const status =
                        value
                            ? "Active"
                            : "Inactive";


                    return (

                        <StatusBadge

                            label={
                                status
                            }

                            variant={
                                getStatusVariant(
                                    status
                                )
                            }

                        />

                    );
                },
        },


        {
            Header:
                "DATE CREATED",

            accessor:
                "created_at",

            Cell:
                ({
                    value,
                }) => (

                    <Text
                        fontSize="sm"
                    >
                        {
                            value
                                ? new Date(
                                    value
                                ).toLocaleDateString()
                                : "N/A"
                        }
                    </Text>

                ),
        },


        {
            Header:
                "ACTIONS",

            accessor:
                "actions",

            Cell:
                ({
                    row,
                }) => (

                    <ThreeDotMenu

                        icon={
                            <EllipsisVertical
                                size={20}
                                color="grey"
                            />
                        }

                        items={
                            generateMenuItems(
                                row
                            )
                        }

                    />

                ),
        },

    ];



    return (

        <Box
            width="100%"
        >


            <Flex
                justifyContent="space-between"
                alignItems="center"
                mb={6}
            >

                <Box>

                    <Text
                        fontSize="xl"
                        fontWeight="700"
                    >
                        Agent Services
                    </Text>

                    <Text
                        color="gray.500"
                        fontSize="sm"
                        mt={1}
                    >
                        Manage the services available for agents to provide to customers.
                    </Text>

                </Box>


                <GradientButton

                    icon={
                        <Plus
                            size={17}
                        />
                    }

                    height="40px"

                    onClick={
                        handleAdd
                    }

                >
                    Add Service

                </GradientButton>

            </Flex>


     
            <Text
                fontSize="md"
                fontWeight="600"
                mb={4}
            >
                All Agent Services:{" "}
                {
                    Array.isArray(
                        services
                    )
                        ? services.length
                        : 0
                }
            </Text>


            <ReusableTable

                columnsData={
                    columnsData
                }

                tableData={
                    Array.isArray(
                        services
                    )
                        ? services
                        : []
                }

                isLoading={
                    loading
                }

                emptyMessage={
                    "No agent services found."
                }

            />


 

            <AddAgentService

                isOpen={
                    isAddOpen
                }

                onClose={() =>
                    setIsAddOpen(
                        false
                    )
                }

            />


            <EditAgentService

                isOpen={
                    isEditOpen
                }

                onClose={() =>
                    setIsEditOpen(
                        false
                    )
                }

                service={
                    selectedService
                }

            />



            <ViewAgentService

                isOpen={
                    isViewOpen
                }

                onClose={() =>
                    setIsViewOpen(
                        false
                    )
                }

                service={
                    selectedService
                }

                onEdit={() => {

                    setIsViewOpen(
                        false
                    );

                    setIsEditOpen(
                        true
                    );

                }}

            />



            <DeleteAgentService

                isOpen={
                    isDeleteOpen
                }

                onClose={() =>
                    setIsDeleteOpen(
                        false
                    )
                }

                onConfirm={
                    handleConfirmDelete
                }

                serviceName={
                    selectedService?.name ||
                    "this service"
                }

            />

        </Box>

    );
};


export default AgentServiceManagement;